// components/ActiveUsersPanel.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import Draggable from "react-draggable";
import api from "../utils/api";
import '../css/ActiveUsersPanel.css';
const LS_KEY_COLLAPSE = "auc-collapsed";
const LS_KEY_POS = "auc-pos"; // ⬅️ 위치 저장

export default function ActiveUsersPanel({
  refreshMs = 60_000,
  showDetailsByDefault = false,
  defaultCollapsed,
}) {
  // 접힘 상태
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof defaultCollapsed === "boolean") return defaultCollapsed;
    try {
      const saved = localStorage.getItem(LS_KEY_COLLAPSE);
      if (saved === "1") return true;
      if (saved === "0") return false;
    } catch {}
    return false;
  });

  // 위치 상태 (로컬스토리지 복원)
  const [pos, setPos] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY_POS);
      if (raw) {
        const { x, y } = JSON.parse(raw);
        if (Number.isFinite(x) && Number.isFinite(y)) return { x, y };
      }
    } catch {}
    // 기본 시작점: 화면 우측 상단 근처
    const w = typeof window !== "undefined" ? window.innerWidth : 1200;
    const h = typeof window !== "undefined" ? window.innerHeight : 800;
    return { x: Math.max(16, w - 360), y: Math.max(16, Math.round(h * 0.18)) };
  });

  // drag용 ref (성능 & 경계계산)
  const nodeRef = useRef(null);

  const [data, setData] = useState({ active: [], now: Date.now() });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("name"); // name | department

  const fetchActive = async () => {
    setErr("");
    try {
      setLoading(true);
      const res = await api.get("/admin/users/active-users");
      setData(res.data || { active: [], now: Date.now() });
    } catch {
      setErr("활성 사용자 목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActive();
    const id = setInterval(fetchActive, refreshMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshMs]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = Array.isArray(data.active) ? [...data.active] : [];

    list.sort((a, b) => {
      if (sortKey === "name") {
        const an = (a.name || a.username || "").toLowerCase();
        const bn = (b.name || b.username || "").toLowerCase();
        return an.localeCompare(bn);
      }
      if (sortKey === "department") {
        const ad = (a.department || "").toLowerCase();
        const bd = (b.department || "").toLowerCase();
        return ad.localeCompare(bd);
      }
      return 0;
    });

    if (!term) return list;

    return list.filter((u) => {
      const text = [u.name, u.username, u.email, u.phone, u.department, u.role]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return text.includes(term);
    });
  }, [data.active, q, sortKey]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(LS_KEY_COLLAPSE, next ? "1" : "0"); } catch {}
      return next;
    });
  };

  // 드래그 끝났을 때 위치 저장
  const onStopDrag = (_e, data) => {
    const next = { x: data.x, y: data.y };
    setPos(next);
    try { localStorage.setItem(LS_KEY_POS, JSON.stringify(next)); } catch {}
  };

  return (
    <Draggable
      nodeRef={nodeRef}
      handle=".auc-header"                 // ⬅️ 헤더만 잡고 이동
      cancel="input,textarea,select,button,.auc-list"
      defaultPosition={pos}               // ⬅️ 저장된 위치 복원
      onStop={onStopDrag}
      bounds="parent"                     // ⬅️ 부모 영역 안에서만 이동
    >
    <div
  ref={nodeRef}
  className="userPanel active-users-card"
  style={{ position: "absolute", top: 0, left: 0, zIndex: 3000, cursor: "default" }}
>
  <div className="auc-header" onDoubleClick={toggleCollapsed}>
    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
      <strong>현재 사용자</strong>
      <span className="auc-count" aria-live="polite">
        {loading ? "…" : `${filtered.length}명`}
      </span>
    </div>

    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <select
        value={sortKey}
        onChange={(e) => setSortKey(e.target.value)}
        className="auc-select"
        title="정렬 기준"
        disabled={collapsed}
      >
        <option value="name">이름/아이디순</option>
        <option value="department">부서순</option>
      </select>

      <button onClick={fetchActive} className="auc-btn" disabled={collapsed} title="새로고침">
        새로고침
      </button>

      <button
        onClick={toggleCollapsed}
        className="auc-btn"
        aria-expanded={!collapsed}
        aria-controls="auc-body"
        title={collapsed ? "펼치기" : "접기"}
      >
        {collapsed ? "＋ 펼치기" : "－ 접기"}
      </button>
    </div>
  </div>

  <div id="auc-body" style={{ display: collapsed ? "none" : "block" }}>
    <div style={{ margin: "8px 10px 12px" }}>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="이름/아이디/부서/이메일/전화 검색…"
        className="auc-search-input"
      />
    </div>

    {err && <div className="auc-error" role="alert">{err}</div>}

    {loading ? (
      <div className="ljt-empty" style={{ padding: 10 }}>불러오는 중…</div>
    ) : filtered.length === 0 ? (
      <div className="ljt-empty" style={{ padding: 10 }}>활성 사용자가 없습니다.</div>
    ) : (
      <ul className="auc-list">
        {filtered.map((u) => (
          <li key={u.userId} className="auc-item">
            <div style={{ display: "flex", alignItems: "center", gap: 12, width: "100%" }}>
              {/* Avatar */}
              <div className="auc-avatar" aria-hidden>
                {(u.name || u.username || u.email || "?")
                  .split(/\s+/).map(p => p[0]?.toUpperCase()).slice(0,2).join("") || "?"}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                {/* 1줄: 이름/아이디/권한 */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span className="auc-name" style={{ fontWeight: 700 }}>
                    {u.name || u.username || u.email || u.userId}
                  </span>

                  {u.username && <span className="auc-username">@{u.username}</span>}

                  {u.role && (
                    <span
                      className="auc-role"
                      data-admin={String(u.role).toUpperCase() === "ADMIN"}
                    >
                      {u.role}
                    </span>
                  )}

                  {u.department && (
                    <span className="auc-pill" title="부서">{u.department}</span>
                  )}
                </div>

                {/* 2줄: 연락처 */}
                {(u.email || u.phone) && (
                  <div style={{ marginTop: 4, display: "flex", gap: 10, flexWrap: "wrap", fontSize: 13 }}>
                    {u.email && <span className="auc-pill" title={u.email}>✉ {u.email}</span>}
                    {u.phone && <span className="auc-pill" title={u.phone}>☎ {maskPhone(u.phone)}</span>}
                  </div>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    )}
  </div>
</div>
    </Draggable>
  );
}

/* ===== 작은 UI 유틸 ===== */
function Avatar({ name }) {
  const initials = (name || "?")
    .split(/\s+/)
    .map((p) => p[0]?.toUpperCase())
    .slice(0, 2)
    .join("");
  return (
    <div
      aria-hidden
      style={{
        width: 36,
        height: 36,
        borderRadius: 999,
        display: "grid",
        placeItems: "center",
        background: "linear-gradient(135deg,#444,#222)",
        fontWeight: 700,
      }}
    >
      {initials || "?"}
    </div>
  );
}

function digitsOnly(s = "") { return String(s).replace(/\D+/g, ""); }
function maskPhone(phone = "") {
  const d = digitsOnly(phone);
  if (!d) return phone;
  if (d.length === 10) return `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6)}`;
  if (d.length === 11) return `${d.slice(0,3)}-${d.slice(3,7)}-${d.slice(7)}`;
  return phone;
}


